import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Genuine per-icon animated lucide icons (in the spirit of lucide-animated.com):
// the icon's INTERNAL geometry animates — not just a CSS scale on the whole glyph.
// Controlled via the `animate` boolean (from a parent row/menu-item hover or active
// state); when `animate` is omitted the icon animates on its own hover. Path data is
// lucide-react v1.27.0 iconNodes (copy / layers / gallery-thumbnails / trending-up-down
// / list-tree), so they match the static icons pixel-for-pixel at rest.

const svgProps = (size, strokeWidth) => ({
  xmlns: 'http://www.w3.org/2000/svg',
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  style: { display: 'block', overflow: 'visible' },
});

const useTrigger = (animate) => {
  const [hover, setHover] = useState(false);
  const controlled = animate !== undefined;
  const on = controlled ? animate : hover;
  const bind = controlled ? {} : {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };
  return { state: on ? 'animate' : 'rest', bind };
};

const spring = { type: 'spring', stiffness: 320, damping: 18 };

// Chevron left / right (lucide chevron-left / chevron-right) — the chevron
// nudges in its own direction on hover/active.
export const AnimatedChevronLeft = ({ size = 20, strokeWidth = 2.4, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="m15 18-6-6 6-6"
        variants={{ rest: { x: 0 }, animate: { x: -2.2 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

export const AnimatedChevronRight = ({ size = 20, strokeWidth = 2.4, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="m9 18 6-6-6-6"
        variants={{ rest: { x: 0 }, animate: { x: 2.2 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Search (lucide search) — the lens nudges + the handle extends on hover.
export const AnimatedSearch = ({ size = 20, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.circle
        cx="11"
        cy="11"
        r="8"
        variants={{ rest: { scale: 1 }, animate: { scale: 0.88 } }}
        transition={spring}
        style={{ transformOrigin: '11px 11px' }}
      />
      <motion.path
        d="m21 21-4.3-4.3"
        variants={{ rest: { x: 0, y: 0 }, animate: { x: 1.6, y: 1.6 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Bookmark-plus (lucide bookmark-plus) — the plus pops on hover.
export const AnimatedBookmarkPlus = ({ size = 20, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
        variants={{ rest: { y: 0 }, animate: { y: -1.4 } }}
        transition={spring}
      />
      <motion.path
        d="M12 7v6"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.2 } }}
        transition={spring}
        style={{ transformOrigin: '12px 10px' }}
      />
      <motion.path
        d="M9 10h6"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.2 } }}
        transition={spring}
        style={{ transformOrigin: '12px 10px' }}
      />
    </motion.svg>
  );
};

// Play (lucide play) — the triangle presses/scales on hover.
export const AnimatedPlay = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.polygon
        points="6 3 20 12 6 21 6 3"
        fill="currentColor"
        stroke="currentColor"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.14 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
    </motion.svg>
  );
};

// Watchlist (lucide bookmark + plus / check) — bookmark lifts; the inner mark
// pops. `checked` swaps the plus for a check (already in watchlist).
export const AnimatedBookmark = ({
  size = 18, strokeWidth = 2.2, animate, checked,
}) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"
        variants={{ rest: { y: 0 }, animate: { y: -1.5 } }}
        transition={spring}
      />
      {checked ? (
        <motion.path
          d="M9 10l2 2 4-4"
          variants={{ rest: { scale: 1 }, animate: { scale: 1.18 } }}
          transition={spring}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        />
      ) : (
        <>
          <motion.line
            x1="12"
            y1="7"
            x2="12"
            y2="12"
            variants={{ rest: { scaleY: 1 }, animate: { scaleY: 1.25 } }}
            transition={spring}
            style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
          />
          <line x1="9.5" y1="9.5" x2="14.5" y2="9.5" />
        </>
      )}
    </motion.svg>
  );
};

// Category — the two "copy" sheets separate diagonally, then spring back.
export const AnimatedCategory = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
        variants={{ rest: { x: 0, y: 0 }, animate: { x: -1.6, y: -1.6 } }}
        transition={spring}
      />
      <motion.rect
        width="14"
        height="14"
        x="8"
        y="8"
        rx="2"
        ry="2"
        variants={{ rest: { x: 0, y: 0 }, animate: { x: 2.4, y: 2.4 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Playlist — the three stacked layers fan apart (top lifts, bottom drops), staggered.
export const AnimatedPlaylist = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z"
        variants={{ rest: { y: 0 }, animate: { y: -2.6 } }}
        transition={spring}
      />
      <motion.path
        d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12"
        variants={{ rest: { y: 0 }, animate: { y: 0.2 } }}
        transition={{ ...spring, delay: 0.03 }}
      />
      <motion.path
        d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17"
        variants={{ rest: { y: 0 }, animate: { y: 2.6 } }}
        transition={{ ...spring, delay: 0.06 }}
      />
    </motion.svg>
  );
};

// Album — the frame eases in slightly while the thumbnail ticks lift in a staggered wave.
export const AnimatedAlbum = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      {/* lucide "image" — photo frame; the sun pulses and the mountain lifts. */}
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <motion.circle
        cx="9"
        cy="9"
        r="2"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.3 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
      <motion.path
        d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
        variants={{ rest: { y: 0 }, animate: { y: -1.6 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Projector (lucide "projector") — the lens pulses and the light rays flick out.
export const AnimatedProjector = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const rays = ['M5 7 3 5', 'M9 6V3', 'm13 7 2-2'];
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      {rays.map((d, i) => (
        <motion.path
          key={d}
          d={d}
          variants={{ rest: { opacity: 0.9, scale: 1 }, animate: { opacity: 0.4, scale: 1.3 } }}
          transition={{ ...spring, delay: i * 0.05 }}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        />
      ))}
      <motion.circle
        cx="9"
        cy="13"
        r="3"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.35 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
      <path d="M11.83 12H20a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h2.17" />
      <path d="M16 16h.01" />
    </motion.svg>
  );
};

// Lock (lucide "lock") — the shackle lifts clearly as if unlocking on hover.
export const AnimatedLock = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <motion.path
        d="M7 11V7a5 5 0 0 1 10 0v4"
        variants={{ rest: { y: 0 }, animate: { y: -2.6 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Layout grid (lucide "layout-grid") — the four tiles pop in a staggered wave.
export const AnimatedLayoutGrid = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const tiles = [
    { x: 3, y: 3 }, { x: 14, y: 3 }, { x: 14, y: 14 }, { x: 3, y: 14 },
  ];
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      {tiles.map((t, i) => (
        <motion.rect
          key={`${t.x}-${t.y}`}
          width="7"
          height="7"
          x={t.x}
          y={t.y}
          rx="1"
          variants={{ rest: { scale: 1 }, animate: { scale: 0.68 } }}
          transition={{ ...spring, delay: i * 0.08 }}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        />
      ))}
    </motion.svg>
  );
};

// Layout dashboard (lucide layout-dashboard) — the four panels settle inward
// with a stagger; the classic "dashboard" glyph for the floating pill shortcut.
export const AnimatedLayoutDashboard = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const tiles = [
    { w: 7, h: 9, x: 3, y: 3 },
    { w: 7, h: 5, x: 14, y: 3 },
    { w: 7, h: 9, x: 14, y: 12 },
    { w: 7, h: 5, x: 3, y: 16 },
  ];
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      {tiles.map((t, i) => (
        <motion.rect
          key={`${t.x}-${t.y}`}
          width={t.w}
          height={t.h}
          x={t.x}
          y={t.y}
          rx="1"
          variants={{ rest: { scale: 1 }, animate: { scale: 0.72 } }}
          transition={{ ...spring, delay: i * 0.07 }}
          style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
        />
      ))}
    </motion.svg>
  );
};

// Help circle (lucide circle-help) — the question mark gives a small confident
// bob on hover; the ring holds. For the pill's "help / start tour" action.
export const AnimatedHelpCircle = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <circle cx="12" cy="12" r="10" />
      <motion.g
        variants={{ rest: { y: 0 }, animate: { y: -1.4 } }}
        transition={spring}
      >
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <path d="M12 17h.01" />
      </motion.g>
    </motion.svg>
  );
};

// Alert triangle (lucide triangle-alert) — the exclamation nudges up for a quick
// "attention" beat; the triangle holds. The retained alert indicator on the pill.
export const AnimatedAlertTriangle = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <motion.g
        variants={{ rest: { y: 0 }, animate: { y: -1.4 } }}
        transition={spring}
      >
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </motion.g>
    </motion.svg>
  );
};

// Log out (lucide log-out) — the arrow slides out through the door on hover;
// the door frame holds. For sign-out actions in settings / account.
export const AnimatedLogOut = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <motion.g
        variants={{ rest: { x: 0 }, animate: { x: 2.6 } }}
        transition={spring}
      >
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" x2="9" y1="12" y2="12" />
      </motion.g>
    </motion.svg>
  );
};

// Trending — the up-trend and down-trend limbs pull apart to emphasise up vs. down.
export const AnimatedTrending = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const down = { rest: { x: 0, y: 0 }, animate: { x: 1.4, y: 1.6 } };
  const up = { rest: { x: 0, y: 0 }, animate: { x: 1.4, y: -1.6 } };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path d="M14.828 14.828 21 21" variants={down} transition={spring} />
      <motion.path d="M21 16v5h-5" variants={down} transition={spring} />
      <motion.path d="m21 3-9 9-4-4-6 6" variants={up} transition={spring} />
      <motion.path d="M21 8V3h-5" variants={up} transition={spring} />
    </motion.svg>
  );
};

// Visibility · Private (hat-glasses) — the incognito lenses pulse, staggered.
export const AnimatedPrivate = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const lens = (delay) => ({
    variants: { rest: { scale: 1 }, animate: { scale: 1.18 } },
    transition: { ...spring, delay },
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  });
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="m19 11-2.11-6.657a2 2 0 0 0-2.752-1.148l-1.276.61A2 2 0 0 1 12 4H8.5a2 2 0 0 0-1.925 1.456L5 11" />
      <path d="M2 11h20" />
      <path d="M14 18a2 2 0 0 0-4 0" />
      <motion.circle cx="7" cy="18" r="3" {...lens(0)} />
      <motion.circle cx="17" cy="18" r="3" {...lens(0.08)} />
    </motion.svg>
  );
};

// Visibility · Group (users) — the second person slides in behind the first.
export const AnimatedGroup = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const back = {
    variants: { rest: { x: 0, opacity: 1 }, animate: { x: [3, 0], opacity: [0.35, 1] } },
    transition: spring,
  };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <motion.circle
        cx="9"
        cy="7"
        r="4"
        variants={{ rest: { scale: 1 }, animate: { scale: 1.12 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
      <motion.path d="M16 3.128a4 4 0 0 1 0 7.744" {...back} />
      <motion.path d="M22 21v-2a4 4 0 0 0-3-3.87" {...back} />
    </motion.svg>
  );
};

// Visibility · All Viewers / public (waypoints) — the connected nodes pulse outward.
export const AnimatedPublic = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const node = (delay) => ({
    variants: { rest: { scale: 1 }, animate: { scale: 1.35 } },
    transition: { ...spring, delay },
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  });
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="m10.586 5.414-5.172 5.172" />
      <path d="m18.586 13.414-5.172 5.172" />
      <path d="M6 12h12" />
      <motion.circle cx="12" cy="4" r="2" {...node(0)} />
      <motion.circle cx="20" cy="12" r="2" {...node(0.06)} />
      <motion.circle cx="12" cy="20" r="2" {...node(0.12)} />
      <motion.circle cx="4" cy="12" r="2" {...node(0.18)} />
    </motion.svg>
  );
};

// Edit (square-pen) — the pen nib wiggles as if writing.
export const AnimatedEdit = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <motion.path
        d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"
        variants={{ rest: { x: 0, y: 0, rotate: 0 }, animate: { x: -1.2, y: 1.2, rotate: -10 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
    </motion.svg>
  );
};

// Download — the arrow dips down into the tray.
export const AnimatedDownload = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const arrow = { variants: { rest: { y: 0 }, animate: { y: 2.4 } }, transition: spring };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <motion.path d="M12 15V3" {...arrow} />
      <motion.path d="m7 10 5 5 5-5" {...arrow} />
    </motion.svg>
  );
};

// Share — the three nodes pop in a staggered pulse.
export const AnimatedShare = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const node = (delay) => ({
    variants: { rest: { scale: 1 }, animate: { scale: 1.22 } },
    transition: { ...spring, delay },
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  });
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
      <motion.circle cx="6" cy="12" r="3" {...node(0)} />
      <motion.circle cx="18" cy="5" r="3" {...node(0.06)} />
      <motion.circle cx="18" cy="19" r="3" {...node(0.12)} />
    </motion.svg>
  );
};

// Trash — the lid tilts open.
export const AnimatedTrash = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const lid = {
    variants: { rest: { rotate: 0, y: 0 }, animate: { rotate: -14, y: -0.5 } },
    transition: spring,
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <motion.path d="M3 6h18" {...lid} />
      <motion.path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" {...lid} />
    </motion.svg>
  );
};

// Subcategory — the tree branches redraw from the spine, top to bottom.
export const AnimatedSubcategory = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const branch = (delay) => ({
    variants: { rest: { pathLength: 1, opacity: 1 }, animate: { pathLength: [0.12, 1], opacity: [0.35, 1] } },
    transition: { duration: 0.42, delay, ease: 'easeOut' },
  });
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M3 5v12a2 2 0 0 0 2 2h3" />
      <path d="M3 10a2 2 0 0 0 2 2h3" />
      <motion.path d="M8 5h13" {...branch(0)} />
      <motion.path d="M13 12h8" {...branch(0.08)} />
      <motion.path d="M13 19h8" {...branch(0.16)} />
    </motion.svg>
  );
};

// Copy — the front sheet slides out from behind, then springs back.
export const AnimatedCopy = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.rect
        width="14"
        height="14"
        x="8"
        y="8"
        rx="2"
        ry="2"
        variants={{ rest: { x: 0, y: 0 }, animate: { x: 1.8, y: 1.8 } }}
        transition={spring}
      />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </motion.svg>
  );
};

// User-round-plus (invite) — the plus sign pulses.
export const AnimatedUserRoundPlus = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const plus = {
    variants: { rest: { scale: 1 }, animate: { scale: 1.3 } },
    transition: spring,
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M2 21a8 8 0 0 1 13.292-6" />
      <circle cx="10" cy="8" r="5" />
      <motion.path d="M19 16v6" {...plus} />
      <motion.path d="M22 19h-6" {...plus} />
    </motion.svg>
  );
};

// X (cancel) — spins a quarter-turn on hover.
export const AnimatedX = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg
      {...svgProps(size, strokeWidth)}
      initial="rest"
      animate={state}
      variants={{ rest: { rotate: 0 }, animate: { rotate: 90 } }}
      transition={spring}
      style={{ display: 'block', overflow: 'visible', transformOrigin: 'center' }}
      {...bind}
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </motion.svg>
  );
};

// Bell (notifications) — swings from its top on hover.
export const AnimatedBell = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.g
        variants={{ rest: { rotate: 0 }, animate: { rotate: [0, -12, 9, -5, 0] } }}
        transition={{ duration: 0.6, ease: 'easeInOut' }}
        style={{ transformBox: 'fill-box', transformOrigin: 'top center' }}
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </motion.g>
    </motion.svg>
  );
};

// Credit card (billing) — the magnetic stripe draws across.
export const AnimatedCreditCard = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <motion.line
        x1="2"
        x2="22"
        y1="10"
        y2="10"
        variants={{ rest: { pathLength: 1, opacity: 1 }, animate: { pathLength: [0.15, 1], opacity: [0.4, 1] } }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      />
    </motion.svg>
  );
};

// Cloud — drifts up and swells slightly.
export const AnimatedCloud = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z"
        variants={{ rest: { y: 0, scale: 1 }, animate: { y: -1.5, scale: 1.05 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
    </motion.svg>
  );
};

// Cloud-upload (lucide cloud-upload) — the arrow + stem lift on hover.
export const AnimatedCloudUpload = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const up = { variants: { rest: { y: 0 }, animate: { y: -2.5 } }, transition: spring };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <motion.path d="M12 12v9" {...up} />
      <motion.path d="m16 16-4-4-4 4" {...up} />
    </motion.svg>
  );
};

// Settings (lucide settings gear) — the gear turns a quarter-rotation on hover.
export const AnimatedSettings = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.g
        variants={{ rest: { rotate: 0 }, animate: { rotate: 90 } }}
        transition={{ type: 'spring', stiffness: 120, damping: 14 }}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </motion.g>
    </motion.svg>
  );
};

// File-pen-line (lucide file-pen-line) — the pen nudges + the underline draws in.
export const AnimatedFilePenLine = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="m18 5-2.414-2.414A2 2 0 0 0 14.172 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6" />
      <motion.path
        d="M21.378 12.626a1 1 0 0 0-3.004-3.004l-4.01 4.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"
        variants={{ rest: { x: 0, y: 0, rotate: 0 }, animate: { x: -1.3, y: -1.3, rotate: -7 } }}
        transition={spring}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
      <motion.path
        d="M8 18h1"
        variants={{ rest: { pathLength: 1, opacity: 1 }, animate: { pathLength: [0.15, 1], opacity: [0.4, 1] } }}
        transition={{ duration: 0.42, ease: 'easeOut' }}
      />
    </motion.svg>
  );
};

// User (account) — the head bobs up on hover.
export const AnimatedUser = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <motion.circle
        cx="12"
        cy="7"
        r="4"
        variants={{ rest: { y: 0 }, animate: { y: -1.5 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// File-text (terms / agreement) — the text lines draw in, staggered.
export const AnimatedFileText = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const line = (delay) => ({
    variants: { rest: { pathLength: 1, opacity: 1 }, animate: { pathLength: [0, 1], opacity: [0.3, 1] } },
    transition: { duration: 0.4, delay, ease: 'easeOut' },
  });
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <motion.path d="M10 9H8" {...line(0)} />
      <motion.path d="M16 13H8" {...line(0.08)} />
      <motion.path d="M16 17H8" {...line(0.16)} />
    </motion.svg>
  );
};

// Gift — a rounded present rather than lucide's flat-bow `gift`: a soft-cornered
// lid band over a rounded box, with the bow drawn as two round loops (the same
// geometric, fully-rounded language as the user-round family). On hover the lid
// and bow lift off the box with the shared spring and the loops swell slightly,
// as if the present is being opened.
export const AnimatedGift = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  const loop = {
    variants: { rest: { scale: 1 }, animate: { scale: 1.12 } },
    transition: spring,
    style: { transformBox: 'fill-box', transformOrigin: 'center' },
  };
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      {/* Box + the ribbon running down it — stays put. */}
      <path d="M5 11.5v7.5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7.5" />
      <path d="M12 11.5V21" />
      {/* Lid + bow lift together. */}
      <motion.g
        variants={{ rest: { y: 0 }, animate: { y: -1.6 } }}
        transition={spring}
      >
        <rect x="3" y="7.5" width="18" height="4" rx="1.5" />
        <path d="M12 7.5v4" />
        <motion.circle cx="9.7" cy="5.2" r="2.1" {...loop} />
        <motion.circle cx="14.3" cy="5.2" r="2.1" {...loop} />
      </motion.g>
    </motion.svg>
  );
};

// Upload (lucide upload) — the arrow lifts up out of the tray on hover/active,
// the motion the action itself implies.
export const AnimatedUpload = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <motion.g
        variants={{ rest: { y: 0 }, animate: { y: -2.4 } }}
        transition={spring}
      >
        <path d="m17 8-5-5-5 5" />
        <path d="M12 3v12" />
      </motion.g>
    </motion.svg>
  );
};

// Film (lucide film) — the strip's sprocket holes shift, like frames advancing.
export const AnimatedFilm = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <motion.g
        variants={{ rest: { y: 0 }, animate: { y: -1.5 } }}
        transition={spring}
      >
        <path d="M7 3v18" />
        <path d="M17 3v18" />
        <path d="M3 7.5h4" />
        <path d="M17 7.5h4" />
        <path d="M3 16.5h4" />
        <path d="M17 16.5h4" />
      </motion.g>
      <path d="M3 12h18" />
    </motion.svg>
  );
};

// Images (lucide images) — the front frame lifts clear of the stack.
export const AnimatedImages = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <path d="M18 22H4a2 2 0 0 1-2-2V6" />
      <path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18" />
      <motion.g
        variants={{ rest: { x: 0, y: 0 }, animate: { x: 1.4, y: -1.4 } }}
        transition={spring}
      >
        <circle cx="12" cy="8" r="2" />
        <rect width="16" height="16" x="6" y="2" rx="2" />
      </motion.g>
    </motion.svg>
  );
};

// Video (lucide video) — the lens barrel pushes out, like a camera starting up.
export const AnimatedVideo = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"
        variants={{ rest: { x: 0 }, animate: { x: 1.8 } }}
        transition={spring}
      />
      <rect x="2" y="6" width="14" height="12" rx="2" />
    </motion.svg>
  );
};

// Smartphone (lucide smartphone) — the handset gives a short buzz.
export const AnimatedSmartphone = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.g
        variants={{ rest: { rotate: 0 }, animate: { rotate: [0, -7, 6, -4, 0] } }}
        transition={{ duration: 0.5 }}
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      >
        <rect width="14" height="20" x="5" y="2" rx="2" ry="2" />
        <path d="M12 18h.01" />
      </motion.g>
    </motion.svg>
  );
};

// TV (lucide tv) — the aerial legs lift towards the set, as if being switched on.
export const AnimatedTv = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <rect width="20" height="15" x="2" y="7" rx="2" ry="2" />
      <motion.path
        d="m17 2-5 5-5-5"
        variants={{ rest: { y: 0 }, animate: { y: 1.6 } }}
        transition={spring}
      />
    </motion.svg>
  );
};

// Graduation cap (lucide graduation-cap) — the cap tips forward and the tassel
// cord swings, the way a mortarboard does when you nod.
export const AnimatedGraduationCap = ({ size = 18, strokeWidth = 2.2, animate }) => {
  const { state, bind } = useTrigger(animate);
  return (
    <motion.svg {...svgProps(size, strokeWidth)} initial="rest" animate={state} {...bind}>
      <motion.path
        d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"
        variants={{ rest: { y: 0 }, animate: { y: -1.4 } }}
        transition={spring}
      />
      <motion.path
        d="M22 10v6"
        variants={{ rest: { rotate: 0 }, animate: { rotate: 9 } }}
        style={{ originX: '22px', originY: '10px' }}
        transition={spring}
      />
      <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
    </motion.svg>
  );
};
